/* @jsx h */
import { computed, defineComponent, h, onMounted, ref, watch } from "vue";
import { setTitle } from "@lemmonhq/orders";
import { useQuery } from "@tanstack/vue-query";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useAuth } from "@app/vue/providers/Auth";
import type { AuditEvent, AuditEventCursor } from "@lemmonhq/types";
import {
  ErrorRenderer,
  Loading,
  ResponsiveTable,
  RightSheet,
} from "@lemmonhq/uikit";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";

const PAGE_SIZE = 30;

const EVENT_ICON_MAP: Record<string, string> = {
  order_created: "receipt_long",
  order_items_updated: "edit_square",
  table_opened: "table_restaurant",
  table_closed: "table_bar",
  reservation_created: "event_available",
  reservation_request_accepted: "task_alt",
  reservation_request_created: "event_note",
  customer_created: "person_add",
  payment_created: "payments",
};

const prettyEventName = (value: string) =>
  value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatTimestamp = (value: string | null) => {
  if (!value) return "";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
};

const toLocalInputValue = (date: Date) => {
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

const fromLocalInputValue = (value: string) => {
  if (!value) return null;
  return new Date(value).toISOString();
};

const summarizePayload = (event: AuditEvent) => {
  const payload = event.payload || {};
  const parts: string[] = [];

  if (payload.order_id) parts.push(`order #${payload.order_id}`);
  if (payload.payment_id) parts.push(`payment #${payload.payment_id}`);
  if (payload.table_id) parts.push(`table #${payload.table_id}`);
  if (payload.reservation_id)
    parts.push(`reservation ${payload.reservation_id}`);
  if (payload.request_id) parts.push(`request ${payload.request_id}`);
  if (payload.customer_id) parts.push(`customer ${payload.customer_id}`);

  return parts.join(" · ");
};

export const AuditSettings = defineComponent({
  name: "AuditSettings",
  setup() {
    const trpc = useTrpc();
    const { user } = useAuth();

    const now = new Date();
    const twelveHoursAgo = new Date(now.getTime() - 12 * 60 * 60 * 1000);

    const selectedEventName = ref<string>("");
    const selectedUserId = ref<string>("");
    const dateFrom = ref<string>(toLocalInputValue(twelveHoursAgo));
    const dateTo = ref<string>("");
    const mobileSheetOpen = ref(false);
    const pendingEventName = ref<string>("");
    const pendingUserId = ref<string>("");
    const pendingDateFrom = ref<string>("");
    const pendingDateTo = ref<string>("");
    const events = ref<AuditEvent[]>([]);
    const cursor = ref<AuditEventCursor | null>(null);
    const hasMore = ref(true);
    const isInitialLoading = ref(true);
    const isLoadingMore = ref(false);
    const loadError = ref<string | null>(null);

    const filtersQuery = useQuery({
      queryKey: ["audit", "filters"],
      queryFn: () => trpc.audit.filters.query(),
    });

    const getAvailableUsers = () => {
      const currentUser = user.value?.id
        ? [
            {
              id: user.value.id,
              name: user.value.profile.name,
              staff_type: user.value.profile.staff_type,
            },
          ]
        : [];
      const colleagues = (user.value?.colleagues || []).map((colleague) => ({
        id: colleague.id,
        name: colleague.profile.name,
        staff_type: colleague.profile.staff_type,
      }));
      const seen = new Set<number>();
      return [...currentUser, ...colleagues].filter((entry) => {
        if (seen.has(entry.id)) return false;
        seen.add(entry.id);
        return true;
      });
    };

    const findActor = (userId: number | null) => {
      if (!userId) return null;
      return getAvailableUsers().find((entry) => entry.id === userId) || null;
    };

    const formatActor = (userId: number | null) => {
      const actor = findActor(userId);
      if (!actor) return userId ? `User #${userId}` : "System";
      if (actor.name?.trim()) return actor.name.trim();
      return `User #${actor.id}`;
    };

    const formatActorRole = (userId: number | null) => {
      return findActor(userId)?.staff_type || null;
    };

    const loadEvents = async (reset = false) => {
      if (reset) {
        isInitialLoading.value = true;
        isLoadingMore.value = false;
        cursor.value = null;
        hasMore.value = true;
        loadError.value = null;
      } else {
        if (isLoadingMore.value || !hasMore.value) return;
        isLoadingMore.value = true;
      }

      try {
        const response = await trpc.audit.list.query({
          name: selectedEventName.value || null,
          user_id: selectedUserId.value ? Number(selectedUserId.value) : null,
          date_from: fromLocalInputValue(dateFrom.value),
          date_to: fromLocalInputValue(dateTo.value),
          limit: PAGE_SIZE,
          cursor: reset ? null : cursor.value,
        });

        events.value = reset
          ? response.data
          : events.value.concat(response.data);
        cursor.value = response.next_cursor;
        hasMore.value = response.has_more;
      } catch (error) {
        console.error("Failed to load audit events", error);
        loadError.value =
          error instanceof Error
            ? error.message
            : "Failed to load audit events";
        if (reset) {
          events.value = [];
        }
      } finally {
        if (reset) {
          isInitialLoading.value = false;
        } else {
          isLoadingMore.value = false;
        }
      }
    };

    const clearFilters = () => {
      selectedEventName.value = "";
      selectedUserId.value = "";
      dateFrom.value = "";
      dateTo.value = "";
    };

    const openMobileSheet = () => {
      pendingEventName.value = selectedEventName.value;
      pendingUserId.value = selectedUserId.value;
      pendingDateFrom.value = dateFrom.value;
      pendingDateTo.value = dateTo.value;
      mobileSheetOpen.value = true;
    };

    const closeMobileSheet = () => {
      mobileSheetOpen.value = false;
    };

    const applyMobileFilters = () => {
      selectedEventName.value = pendingEventName.value;
      selectedUserId.value = pendingUserId.value;
      dateFrom.value = pendingDateFrom.value;
      dateTo.value = pendingDateTo.value;
      mobileSheetOpen.value = false;
    };

    const clearMobileFilters = () => {
      pendingEventName.value = "";
      pendingUserId.value = "";
      pendingDateFrom.value = "";
      pendingDateTo.value = "";
      clearFilters();
      mobileSheetOpen.value = false;
    };

    const activeFilterCount = computed(() => {
      let count = 0;
      if (selectedEventName.value) count++;
      if (selectedUserId.value) count++;
      if (dateFrom.value) count++;
      if (dateTo.value) count++;
      return count;
    });

    const pendingFilterCount = computed(() => {
      let count = 0;
      if (pendingEventName.value) count++;
      if (pendingUserId.value) count++;
      if (pendingDateFrom.value) count++;
      if (pendingDateTo.value) count++;
      return count;
    });

    watch([selectedEventName, selectedUserId, dateFrom, dateTo], async () => {
      await loadEvents(true);
    });

    const onScroll = (event: Event) => {
      const el = event.currentTarget as HTMLElement;
      if (!el) return;
      if (
        el.scrollHeight - el.scrollTop - el.clientHeight < 300 &&
        hasMore.value &&
        !isLoadingMore.value
      ) {
        void loadEvents(false);
      }
    };

    onMounted(async () => {
      setTitle("Audit Log");
      await loadEvents(true);
    });

    const columns: ResponsiveTableColumn[] = [
      {
        key: "d-event",
        label: "Event",
        icon: "history",
        mobilePriority: "hidden",
        headerClass: "w-[200px] max-w-[200px]",
        cellClass: "w-[200px] max-w-[200px]",
        render: (event: AuditEvent) => (
          <div class="flex min-w-0 items-center gap-3">
            <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-base-200 text-base-content/65">
              <span class="material-symbols-outlined text-[18px]">
                {EVENT_ICON_MAP[event.name] || "history"}
              </span>
            </div>
            <div class="min-w-0">
              <div class="truncate font-medium text-base-content">
                {prettyEventName(event.name)}
              </div>
              {summarizePayload(event) ? (
                <div class="truncate text-xs text-base-content/60">
                  {summarizePayload(event)}
                </div>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        key: "d-user",
        label: "User",
        icon: "person",
        mobilePriority: "hidden",
        render: (event: AuditEvent) => (
          <div class="min-w-0">
            <div class="truncate text-sm font-medium text-base-content/85">
              {formatActor(event.user_id)}
            </div>
            {formatActorRole(event.user_id) ? (
              <div class="truncate text-xs text-base-content/50">
                {formatActorRole(event.user_id)}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: "d-created",
        label: "Created",
        icon: "schedule",
        mobilePriority: "hidden",
        render: (event: AuditEvent) => (
          <span class="text-sm text-base-content/70">
            {formatTimestamp(event.created_at)}
          </span>
        ),
      },
      {
        key: "d-transaction",
        label: "Transaction",
        icon: "tag",
        mobilePriority: "hidden",
        render: (event: AuditEvent) =>
          event.tx_id ? (
            <span class="font-mono text-xs text-base-content/55">
              {event.tx_id}
            </span>
          ) : (
            <span class="text-sm text-base-content/35">-</span>
          ),
      },
      {
        key: "d-payload",
        label: "",
        mobilePriority: "hidden",
        headerClass: "w-px",
        cellClass: "text-right whitespace-nowrap",
        render: (event: AuditEvent) =>
          event.payload ? (
            <details
              class="dropdown dropdown-end"
              onClick={(clickEvent: MouseEvent) => clickEvent.stopPropagation()}
            >
              <summary class="btn btn-secondary btn-sm">
                <span class="material-symbols-outlined text-lg">
                  data_object
                </span>
              </summary>
              <pre class="dropdown-content z-20 mt-2 max-h-80 w-96 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-base-200 p-3 text-left text-xs leading-5 text-base-content shadow-xl">
                {JSON.stringify(event.payload, null, 2)}
              </pre>
            </details>
          ) : null,
      },
      {
        key: "m-event",
        label: "Event",
        desktopHide: true,
        mobilePriority: "primary",
        render: (event: AuditEvent) => (
          <div class="flex min-w-0 items-center gap-2">
            <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-base-100 text-base-content/65">
              <span class="material-symbols-outlined text-[18px]">
                {EVENT_ICON_MAP[event.name] || "history"}
              </span>
            </div>
            <div class="min-w-0">
              <div class="truncate text-sm font-medium text-base-content">
                {prettyEventName(event.name)}
              </div>
              <div class="truncate text-xs text-base-content/60">
                {formatActor(event.user_id)}
              </div>
            </div>
          </div>
        ),
      },
      {
        key: "m-created",
        label: "Created",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (event: AuditEvent) => formatTimestamp(event.created_at),
      },
      {
        key: "m-summary",
        label: "Summary",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (event: AuditEvent) =>
          summarizePayload(event) || (event.tx_id ? `tx ${event.tx_id}` : "-"),
      },
      {
        key: "m-payload",
        label: "",
        desktopHide: true,
        mobilePriority: "actions",
        render: (event: AuditEvent) =>
          event.payload ? (
            <details
              onClick={(clickEvent: MouseEvent) => clickEvent.stopPropagation()}
            >
              <summary class="btn btn-secondary btn-xs w-full justify-center">
                Payload
              </summary>
              <pre class="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-base-100 p-3 text-left text-xs leading-5 text-base-content/75">
                {JSON.stringify(event.payload, null, 2)}
              </pre>
            </details>
          ) : null,
      },
    ];

    return () => (
      <div class="flex flex-1 flex-col">
        <AppSubpageHeader
          icon="history"
          title="Audit Log"
          description="Filter by event, user, and timestamp range."
        >
          {{
            actions: () => (
              <button
                class="btn btn-sm btn-ghost relative gap-1.5 pl-2 pr-3 md:hidden"
                onClick={openMobileSheet}
              >
                <span class="material-symbols-outlined text-base">tune</span>
                <span class="text-sm font-medium">Filters</span>
                {activeFilterCount.value > 0 && (
                  <span class="badge badge-primary badge-xs absolute -right-1 -top-1 h-4 min-w-[16px] text-[10px]">
                    {activeFilterCount.value}
                  </span>
                )}
              </button>
            ),
            bottom: () => (
              <div class="hidden items-end gap-3 overflow-x-auto border-t  py-3 md:flex md:flex-nowrap">
                <label class="form-control">
                  <div class="label py-0 pb-1">
                    <span class="label-text text-[11px] uppercase tracking-[0.2em] text-base-content/45">
                      Event
                    </span>
                  </div>
                  <select
                    class="select select-sm select-bordered w-48 bg-base-100"
                    value={selectedEventName.value}
                    onChange={(event) =>
                      (selectedEventName.value = (
                        event.target as HTMLSelectElement
                      ).value)
                    }
                  >
                    <option value="">All events</option>
                    {(filtersQuery.data.value?.names || []).map(
                      (name: string) => (
                        <option key={name} value={name}>
                          {prettyEventName(name)}
                        </option>
                      ),
                    )}
                  </select>
                </label>

                <label class="form-control">
                  <div class="label py-0 pb-1">
                    <span class="label-text text-[11px] uppercase tracking-[0.2em] text-base-content/45">
                      User
                    </span>
                  </div>
                  <select
                    class="select select-sm select-bordered w-48 bg-base-100"
                    value={selectedUserId.value}
                    onChange={(event) =>
                      (selectedUserId.value = (
                        event.target as HTMLSelectElement
                      ).value)
                    }
                  >
                    <option value="">All users</option>
                    {getAvailableUsers().map((actor) => (
                      <option key={actor.id} value={String(actor.id)}>
                        {actor.name || `User #${actor.id}`}
                      </option>
                    ))}
                  </select>
                </label>

                <label class="form-control">
                  <div class="label py-0 pb-1">
                    <span class="label-text text-[11px] uppercase tracking-[0.2em] text-base-content/45">
                      From
                    </span>
                  </div>
                  <input
                    type="datetime-local"
                    class="input input-sm input-bordered w-56 bg-base-100"
                    value={dateFrom.value}
                    onInput={(event) =>
                      (dateFrom.value = (
                        event.target as HTMLInputElement
                      ).value)
                    }
                  />
                </label>

                <label class="form-control">
                  <div class="label py-0 pb-1">
                    <span class="label-text text-[11px] uppercase tracking-[0.2em] text-base-content/45">
                      To
                    </span>
                  </div>
                  <input
                    type="datetime-local"
                    class="input input-sm input-bordered w-56 bg-base-100"
                    value={dateTo.value}
                    onInput={(event) =>
                      (dateTo.value = (event.target as HTMLInputElement).value)
                    }
                  />
                </label>

                <div class="flex shrink-0 items-end">
                  <button class="btn btn-sm btn-ghost" onClick={clearFilters}>
                    Clear Filters
                  </button>
                </div>
              </div>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent noPadding onScroll={onScroll}>
            {isInitialLoading.value || filtersQuery.isPending.value ? (
              <div class="flex h-80 items-center justify-center">
                <Loading />
              </div>
            ) : loadError.value || filtersQuery.isError.value ? (
              <ErrorRenderer
                message="Failed to load audit events"
                onRetry={() => {
                  void filtersQuery.refetch();
                  void loadEvents(true);
                }}
              />
            ) : events.value.length === 0 ? (
              <div class="flex min-h-[320px] flex-col items-center justify-center px-8 py-16 text-center">
                <div class="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-base-200">
                  <span class="material-symbols-outlined text-3xl text-base-content/40">
                    history
                  </span>
                </div>
                <p class="mb-2 text-base font-medium text-base-content">
                  No audit events found
                </p>
                <p class="max-w-md text-sm text-base-content/60">
                  Try widening the date range or clearing filters.
                </p>
              </div>
            ) : (
              <>
                <ResponsiveTable
                  columns={columns}
                  rows={events.value}
                  rowKey={(event: AuditEvent) => event.id}
                  loadingMore={isLoadingMore.value}
                  emptyMessage="No audit events found"
                />
              </>
            )}
          </AppSubpageContent>
        </AppSubpage>

        {mobileSheetOpen.value && (
          <RightSheet
            isOpen={true}
            title="Filters"
            onClose={closeMobileSheet}
            width="320px"
          >
            <div class="flex flex-col gap-5 p-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold uppercase tracking-wider text-base-content/50">
                  Event
                </label>
                <select
                  class="select select-sm select-bordered w-full bg-base-100"
                  value={pendingEventName.value}
                  onChange={(event) =>
                    (pendingEventName.value = (
                      event.target as HTMLSelectElement
                    ).value)
                  }
                >
                  <option value="">All events</option>
                  {(filtersQuery.data.value?.names || []).map(
                    (name: string) => (
                      <option key={name} value={name}>
                        {prettyEventName(name)}
                      </option>
                    ),
                  )}
                </select>
              </div>

              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold uppercase tracking-wider text-base-content/50">
                  User
                </label>
                <select
                  class="select select-sm select-bordered w-full bg-base-100"
                  value={pendingUserId.value}
                  onChange={(event) =>
                    (pendingUserId.value = (
                      event.target as HTMLSelectElement
                    ).value)
                  }
                >
                  <option value="">All users</option>
                  {getAvailableUsers().map((actor) => (
                    <option key={actor.id} value={String(actor.id)}>
                      {actor.name || `User #${actor.id}`}
                    </option>
                  ))}
                </select>
              </div>

              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold uppercase tracking-wider text-base-content/50">
                  From
                </label>
                <input
                  type="datetime-local"
                  class="input input-sm input-bordered w-full bg-base-100"
                  value={pendingDateFrom.value}
                  onInput={(event) =>
                    (pendingDateFrom.value = (
                      event.target as HTMLInputElement
                    ).value)
                  }
                />
              </div>

              <div class="flex flex-col gap-1.5">
                <label class="text-xs font-semibold uppercase tracking-wider text-base-content/50">
                  To
                </label>
                <input
                  type="datetime-local"
                  class="input input-sm input-bordered w-full bg-base-100"
                  value={pendingDateTo.value}
                  onInput={(event) =>
                    (pendingDateTo.value = (
                      event.target as HTMLInputElement
                    ).value)
                  }
                />
              </div>

              <div class="flex gap-2 pt-1">
                <button
                  class="btn btn-primary btn-sm flex-1"
                  onClick={applyMobileFilters}
                >
                  <span class="material-symbols-outlined text-base">check</span>
                  Apply
                  {pendingFilterCount.value > 0 && (
                    <span class="badge badge-sm badge-primary-content ml-1">
                      {pendingFilterCount.value}
                    </span>
                  )}
                </button>
                {pendingFilterCount.value > 0 && (
                  <button
                    class="btn btn-ghost btn-sm text-error"
                    onClick={clearMobileFilters}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </RightSheet>
        )}
      </div>
    );
  },
});
